import React, { useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { formatPricePKR } from '../utils/catalog';

const ComparisonResults = () => {
  const { comparisonResults, showDifferences, openInquiryForm, requestTestDrive } = useContext(AppContext);

  if (!comparisonResults) return null;

  const { vehicle_a, vehicle_b, fields } = comparisonResults;

  return (
    <section className="comparison-results visible" aria-label="Vehicle comparison specifications">
      <div className="comparison-header-bar comparison-header-strip">
        <h3>
          <i className="fas fa-scale-balanced" /> Side-by-Side Comparison
        </h3>
        <span className="comparison-badge comparison-subtitle">
          Comparing <strong>{vehicle_a.name}</strong> vs <strong>{vehicle_b.name}</strong>
        </span>
      </div>

      <div className="comparison-table-scroll-container">
        <table className="comparison-table">
          <thead>
            <tr>
              <th className="comp-col-spec comp-header-cell" scope="col">
                <span className="comp-header-label comp-spec-header-title">Specification</span>
              </th>
              <th className="comp-col-vehicle vehicle-a-col comp-header-cell comp-head-a" scope="col">
                <div className="comp-vehicle-card-head">
                  <div className="comp-thumb-box">
                    {vehicle_a.image_url ? (
                      <img
                        src={vehicle_a.image_url}
                        alt={vehicle_a.name}
                        className="comp-vehicle-thumb"
                      />
                    ) : (
                      <span className="comp-fallback-emoji">{vehicle_a.logo || '🚗'}</span>
                    )}
                  </div>
                  <strong className="comp-vehicle-title">{vehicle_a.name}</strong>
                  <div className="comp-cta-group">
                    <button
                      type="button"
                      className="comp-btn comp-btn-primary action-btn"
                      onClick={() =>
                        openInquiryForm({
                          title: 'Request Info',
                          vehicle: vehicle_a,
                          source: 'comparison',
                        })
                      }
                      title={`Request info about ${vehicle_a.name}`}
                    >
                      <i className="fas fa-envelope" /> Request Info
                    </button>
                    <button
                      type="button"
                      className="comp-btn comp-btn-secondary action-btn secondary"
                      onClick={() =>
                        requestTestDrive({
                          title: 'Book a Test Drive',
                          applicationType: 'testdrive',
                          vehicle: vehicle_a,
                          source: 'comparison',
                        })
                      }
                      title={`Book a test drive for ${vehicle_a.name}`}
                    >
                      <i className="fas fa-key" /> Test Drive
                    </button>
                  </div>
                </div>
              </th>
              <th className="comp-col-vehicle vehicle-b-col comp-header-cell comp-head-b" scope="col">
                <div className="comp-vehicle-card-head">
                  <div className="comp-thumb-box">
                    {vehicle_b.image_url ? (
                      <img
                        src={vehicle_b.image_url}
                        alt={vehicle_b.name}
                        className="comp-vehicle-thumb"
                      />
                    ) : (
                      <span className="comp-fallback-emoji">{vehicle_b.logo || '🚗'}</span>
                    )}
                  </div>
                  <strong className="comp-vehicle-title">{vehicle_b.name}</strong>
                  <div className="comp-cta-group">
                    <button
                      type="button"
                      className="comp-btn comp-btn-primary action-btn"
                      onClick={() =>
                        openInquiryForm({
                          title: 'Request Info',
                          vehicle: vehicle_b,
                          source: 'comparison',
                        })
                      }
                      title={`Request info about ${vehicle_b.name}`}
                    >
                      <i className="fas fa-envelope" /> Request Info
                    </button>
                    <button
                      type="button"
                      className="comp-btn comp-btn-secondary action-btn secondary"
                      onClick={() =>
                        requestTestDrive({
                          title: 'Book a Test Drive',
                          applicationType: 'testdrive',
                          vehicle: vehicle_b,
                          source: 'comparison',
                        })
                      }
                      title={`Book a test drive for ${vehicle_b.name}`}
                    >
                      <i className="fas fa-key" /> Test Drive
                    </button>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => {
              const isDifferent = field.a !== field.b;
              if (showDifferences && !isDifferent) return null;

              const isPrice = field.label && field.label.toLowerCase() === 'price';

              const renderCellValue = (val, label) => {
                if (!val || val === '—' || val === 'N/A') return '—';
                const lower = (label || '').toLowerCase();
                if (lower === 'price') return formatPricePKR(val);
                if (lower === 'available colors') {
                  const colors = String(val)
                    .split(',')
                    .map((c) => c.trim())
                    .filter(Boolean);
                  if (colors.length === 0) return '—';
                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {colors.map((c, i) => (
                        <span
                          key={i}
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            background: '#eef4f8',
                            border: '1px solid #c9dbe8',
                            color: '#0b3d54',
                            fontWeight: 500,
                          }}
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  );
                }
                if (lower.includes('pricing note') || lower.includes('conditions')) {
                  return (
                    <div
                      style={{
                        background: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        fontSize: '12px',
                        color: '#92400e',
                        lineHeight: 1.45,
                      }}
                    >
                      <i className="fas fa-circle-exclamation" style={{ marginRight: '5px', color: '#b45309' }} />
                      {val}
                    </div>
                  );
                }
                return val;
              };

              return (
                <tr
                  key={index}
                  className={`comp-table-row ${isDifferent ? 'comp-row-different' : ''}`}
                >
                  <th scope="row" className="comp-cell-label">
                    {field.label}
                  </th>
                  <td className={`comp-cell-value comp-val-a ${isPrice ? 'comp-cell-price' : ''}`}>
                    {renderCellValue(field.a, field.label)}
                  </td>
                  <td className={`comp-cell-value comp-val-b ${isPrice ? 'comp-cell-price' : ''}`}>
                    {renderCellValue(field.b, field.label)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(vehicle_a.pricing_notes || vehicle_b.pricing_notes) && (
        <div
          className="comparison-pricing-notes-banner"
          style={{
            marginTop: '16px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '12px',
          }}
        >
          {vehicle_a.pricing_notes && (
            <div
              style={{
                background: '#fffbeb',
                border: '1.5px solid #fde68a',
                borderRadius: '8px',
                padding: '12px 14px',
                color: '#92400e',
              }}
            >
              <strong style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', color: '#b45309', fontSize: '13px' }}>
                <i className="fas fa-circle-exclamation" /> {vehicle_a.name} — Pricing Notes & Surcharges
              </strong>
              <div style={{ fontSize: '12px', lineHeight: 1.5, color: '#78350f' }}>{vehicle_a.pricing_notes}</div>
            </div>
          )}
          {vehicle_b.pricing_notes && (
            <div
              style={{
                background: '#fffbeb',
                border: '1.5px solid #fde68a',
                borderRadius: '8px',
                padding: '12px 14px',
                color: '#92400e',
              }}
            >
              <strong style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', color: '#b45309', fontSize: '13px' }}>
                <i className="fas fa-circle-exclamation" /> {vehicle_b.name} — Pricing Notes & Surcharges
              </strong>
              <div style={{ fontSize: '12px', lineHeight: 1.5, color: '#78350f' }}>{vehicle_b.pricing_notes}</div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ComparisonResults;
